/**
 * Reusable character counter for fields with a max length (e.g. description, text with maxChar).
 * Use with getCharacterLimit() from validation so validation (Zod) and UI stay in sync.
 */

import { Typography, type SxProps, type Theme } from '@mui/material';

export type CharacterCounterProps = {
  /** Current character count (e.g. value.length). */
  currentLength: number;
  /** Max characters (from getCharacterLimit(col) or column maxChar). */
  maxChar: number;
  /** Whether to show error state (red color) when at/over limit. */
  isError?: boolean;
  /** Optional sx overrides for custom positioning. */
  sx?: SxProps<Theme>;
};

/** Default styles: absolute position at bottom-left inside a relative container. */
const defaultSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: 10,
  left: 9,
};

/**
 * Renders "current/max" (e.g. "0/500") with caption/secondary styling.
 * By default, positioned absolutely at bottom-left (use inside a Box with position: relative).
 * Pass sx prop to override positioning for other layouts.
 */
export const CharacterCounter = ({
  currentLength,
  maxChar,
  isError = false,
  sx,
}: CharacterCounterProps): React.ReactElement => {
  return (
    <Typography
      variant="caption"
      color={isError ? 'error' : 'text.disabled'}
      sx={[defaultSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {currentLength}/{maxChar}
    </Typography>
  );
};
